import type { ComponentProps, ReactNode } from "react";

export function FieldError({
  className,
  error,
  id,
}: {
  className?: string;
  error: string | undefined;
  id: string;
}) {
  return error ? (
    <span
      className={`lockerverse-form__error ${className ?? ""}`}
      id={id}
      role="alert"
    >
      {error}
    </span>
  ) : null;
}

export function FormField({
  children,
  error,
  errorId,
  id,
  label,
}: {
  children: ReactNode;
  error?: string | undefined;
  errorId?: string | undefined;
  id: string;
  label: string;
}) {
  return (
    <div className="lockerverse-form__field">
      <label htmlFor={id}>{label}</label>
      {children}
      <FieldError error={error} id={errorId ?? `${id}-error`} />
    </div>
  );
}

type TextFieldProps = ComponentProps<"input"> & {
  error?: string | undefined;
  errorId?: string | undefined;
  id: string;
  label: string;
};

export function TextField({
  className,
  error,
  errorId,
  id,
  label,
  ...inputProps
}: TextFieldProps) {
  const messageId = errorId ?? `${id}-error`;
  return (
    <FormField error={error} errorId={messageId} id={id} label={label}>
      <input
        {...inputProps}
        aria-describedby={error ? messageId : inputProps["aria-describedby"]}
        aria-invalid={error ? "true" : inputProps["aria-invalid"]}
        className={`lockerverse-form__control ${className ?? ""}`}
        id={id}
      />
    </FormField>
  );
}

export function EmailField(props: Omit<TextFieldProps, "type">) {
  return (
    <TextField
      autoComplete="email"
      placeholder="name@example.com"
      {...props}
      type="email"
    />
  );
}

/** Native checkbox semantics with the shared SDK control appearance. */
export function Checkbox({
  className,
  ...props
}: Omit<ComponentProps<"input">, "type">) {
  return (
    <input
      {...props}
      className={["lockerverse-form__checkbox", className]
        .filter(Boolean)
        .join(" ")}
      type="checkbox"
    />
  );
}
